import { useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { TARIFA_OPTIONS } from '../../../lib/utils/tarifas';

const CustomersPage = () => {
  const [registered, setRegistered] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'name' | 'orders' | 'spent'>('name');
  const [updatingTarifa, setUpdatingTarifa] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const res = await api.users.getCommercialCustomers();
      if (res.success) setRegistered(res.data || []);
      setLoading(false);
    };
    load();
  }, []);

  const handleTarifaChange = async (customerId: string, tarifa: number) => {
    setUpdatingTarifa(customerId);
    try {
      const res = await api.users.updateCustomerTarifa(customerId, tarifa);
      if (res.success) {
        setRegistered(prev => prev.map(c =>
          (c.id || c._id) === customerId ? { ...c, tarifa } : c
        ));
      }
    } catch (err) {
      console.error('Error updating tarifa:', err);
    } finally {
      setUpdatingTarifa(null);
    }
  };

  const filtered = useMemo(() => {
    const list = q
      ? registered.filter((c) => [c.name, c.email, c.nif].filter(Boolean).some((v: string) => v.toLowerCase().includes(q.toLowerCase())))
      : registered;
    const withStats = list.map((c) => ({
      ...c,
      orders: c.orders || 0,
      totalSpent: c.totalSpent || 0
    }));
    switch (sort) {
      case 'orders':
        return [...withStats].sort((a, b) => (b.orders || 0) - (a.orders || 0));
      case 'spent':
        return [...withStats].sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0));
      default:
        return [...withStats].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  }, [q, registered, sort]);

  const exportCSV = () => {
    const rows = filtered.map((c: any) => ({
      nombre: c.name,
      email: c.email,
      nif: c.nif || '',
      tarifa: c.tarifa || '',
      pedidos: c.orders || 0,
      gasto_total: c.totalSpent || 0
    }));
    const header = Object.keys(rows[0] || { nombre: '', email: '' }).join(',');
    const lines = rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clientes_registrados.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Clientes</h1>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar cliente..."
            className="border rounded px-3 py-2"
          />
          <select value={sort} onChange={(e) => setSort(e.target.value as any)} className="border rounded px-3 py-2">
            <option value="name">Nombre</option>
            <option value="orders">Pedidos</option>
            <option value="spent">Gasto total</option>
          </select>
          <button onClick={exportCSV} className="px-3 py-2 border rounded">Exportar CSV</button>
        </div>
      </div>
      {loading ? (
        <div className="animate-pulse h-6 w-32 bg-gray-200 rounded" />
      ) : (
        <div className="grid gap-3">
          {filtered.length === 0 && <p className="text-gray-500">Sin resultados</p>}
          {filtered.map((c) => {
            const cId = c.id || c._id;
            return (
              <div key={cId} className="border rounded p-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-sm text-gray-600">{c.email}{c.nif ? ` · ${c.nif}` : ''}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <select
                    value={c.tarifa || ''}
                    onChange={(e) => handleTarifaChange(cId, Number(e.target.value))}
                    disabled={updatingTarifa === cId}
                    className={`border rounded px-2 py-1.5 text-sm ${updatingTarifa === cId ? 'opacity-50' : ''}`}
                  >
                    <option value="" disabled>Tarifa...</option>
                    {TARIFA_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.shortLabel}</option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 text-right whitespace-nowrap">
                    {c.orders || 0} pedidos · {c.totalSpent ? `${c.totalSpent.toFixed?.(2)}\u20AC` : '0\u20AC'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
